import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Mail,
  Calendar,
  ChevronDown,
  X,
  Save,
  ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { useAuth } from '../auth/AuthContext';

const supabase = getSupabaseAdminClient();

interface FAQCategory {
  id: string;
  name: string;
}

interface ContactRequest {
  id: string;
  user_id: string | null;
  category_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  subject: string | null;
  message: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  admin_notes: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
}

const statusConfig = {
  pending: { label: 'Offen', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
  in_progress: { label: 'In Bearbeitung', icon: AlertCircle, color: 'text-blue-600', bg: 'bg-blue-50' },
  resolved: { label: 'Gelöst', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  closed: { label: 'Geschlossen', icon: XCircle, color: 'text-slate-600', bg: 'bg-slate-50' },
};

export function ContactRequestsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedRequest, setSelectedRequest] = useState<ContactRequest | null>(null);

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['admin-faq-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('faq_categories')
        .select('id, name')
        .order('sort_order');
      if (error) throw error;
      return data as FAQCategory[];
    },
  });

  // Fetch contact requests (only FAQ contact - without feedback_category_id)
  const { data: requests, isLoading } = useQuery({
    queryKey: ['admin-contact-requests', statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('contact_requests')
        .select('*')
        .is('feedback_category_id', null)
        .order('created_at', { ascending: false });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ContactRequest[];
    },
  });

  // Update request mutation
  const updateRequest = useMutation({
    mutationFn: async (data: { id: string; status: string; admin_notes?: string }) => {
      const updates: Record<string, unknown> = {
        status: data.status,
        admin_notes: data.admin_notes,
      };

      if (data.status === 'resolved' || data.status === 'closed') {
        updates.resolved_at = new Date().toISOString();
        updates.resolved_by = user?.id;
      }

      const { error } = await (supabase.from('contact_requests') as any)
        .update(updates)
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contact-requests'] });
      toast.success('Anfrage aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'Sonstiges';
    return categories?.find(c => c.id === categoryId)?.name || 'Unbekannt';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Stats
  const pendingCount = requests?.filter(r => r.status === 'pending').length || 0;
  const inProgressCount = requests?.filter(r => r.status === 'in_progress').length || 0;
  const resolvedCount = requests?.filter(r => r.status === 'resolved').length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Kontaktanfragen</h1>
            <p className="text-slate-500 mt-1">
              Eingehende Anfragen vom FAQ-Kontaktformular
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-900">{requests?.length || 0}</div>
            <div className="text-sm text-slate-500">Gesamt</div>
          </div>
          <div
            className={`rounded-lg p-4 cursor-pointer transition-colors ${
              statusFilter === 'pending' ? 'bg-amber-100 ring-2 ring-amber-500' : 'bg-amber-50 hover:bg-amber-100'
            }`}
            onClick={() => setStatusFilter(statusFilter === 'pending' ? '' : 'pending')}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="text-2xl font-bold text-amber-700">{pendingCount}</span>
            </div>
            <div className="text-sm text-amber-600">Offen</div>
          </div>
          <div
            className={`rounded-lg p-4 cursor-pointer transition-colors ${
              statusFilter === 'in_progress' ? 'bg-blue-100 ring-2 ring-blue-500' : 'bg-blue-50 hover:bg-blue-100'
            }`}
            onClick={() => setStatusFilter(statusFilter === 'in_progress' ? '' : 'in_progress')}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold text-blue-700">{inProgressCount}</span>
            </div>
            <div className="text-sm text-blue-600">In Bearbeitung</div>
          </div>
          <div
            className={`rounded-lg p-4 cursor-pointer transition-colors ${
              statusFilter === 'resolved' ? 'bg-green-100 ring-2 ring-green-500' : 'bg-green-50 hover:bg-green-100'
            }`}
            onClick={() => setStatusFilter(statusFilter === 'resolved' ? '' : 'resolved')}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-2xl font-bold text-green-700">{resolvedCount}</span>
            </div>
            <div className="text-sm text-green-600">Gelöst</div>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : requests?.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Keine Kontaktanfragen vorhanden</p>
          </div>
        ) : (
          requests?.map((request) => {
            const status = statusConfig[request.status];
            const StatusIcon = status.icon;

            return (
              <div
                key={request.id}
                className="p-4 hover:bg-slate-50 cursor-pointer"
                onClick={() => setSelectedRequest(request)}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg ${status.bg} flex items-center justify-center flex-shrink-0`}>
                    <StatusIcon className={`w-5 h-5 ${status.color}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900">
                        {request.first_name} {request.last_name}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                      {request.message}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        {request.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(request.created_at)}
                      </span>
                      <span className="bg-slate-100 px-2 py-0.5 rounded">
                        {getCategoryName(request.category_id)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          categoryName={getCategoryName(selectedRequest.category_id)}
          onClose={() => setSelectedRequest(null)}
          onUpdate={(data) => {
            updateRequest.mutate({ id: selectedRequest.id, ...data });
            setSelectedRequest(null);
          }}
          isUpdating={updateRequest.isPending}
        />
      )}
    </div>
  );
}

// Detail Modal Component
interface RequestDetailModalProps {
  request: ContactRequest;
  categoryName: string;
  onClose: () => void;
  onUpdate: (data: { status: string; admin_notes?: string }) => void;
  isUpdating: boolean;
}

function RequestDetailModal({
  request,
  categoryName,
  onClose,
  onUpdate,
  isUpdating
}: RequestDetailModalProps) {
  const [status, setStatus] = useState(request.status);
  const [adminNotes, setAdminNotes] = useState(request.admin_notes || '');

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate({ status, admin_notes: adminNotes || undefined });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-900">Anfrage-Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* User Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Kontaktdaten</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{request.first_name} {request.last_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a
                    href={`mailto:${request.email}`}
                    className="text-teal-600 hover:underline"
                  >
                    {request.email}
                  </a>
                </div>
                {request.user_id && (
                  <Link
                    to={`/users?highlight=${request.user_id}`}
                    className="inline-flex items-center gap-1 text-sm text-teal-600 hover:underline mt-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Zur Benutzerverwaltung
                  </Link>
                )}
              </div>
            </div>

            {/* Request Info */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-slate-500">Kategorie:</span>
                <span className="bg-slate-100 px-2 py-0.5 rounded text-sm">{categoryName}</span>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-medium text-slate-500">Erstellt:</span>
                <span className="text-sm text-slate-600">{formatDate(request.created_at)}</span>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-slate-500 mb-2">Nachricht</h3>
                <p className="text-slate-700 whitespace-pre-wrap">{request.message}</p>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Status
              </label>
              <div className="relative">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ContactRequest['status'])}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg appearance-none
                             focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                >
                  <option value="pending">Offen</option>
                  <option value="in_progress">In Bearbeitung</option>
                  <option value="resolved">Gelöst</option>
                  <option value="closed">Geschlossen</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Admin-Notizen (intern)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={4}
                placeholder="Interne Notizen zur Anfrage..."
                className="w-full px-4 py-2 border border-slate-200 rounded-lg resize-none
                           focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            {request.resolved_at && (
              <div className="text-sm text-slate-500">
                Gelöst am: {formatDate(request.resolved_at)}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                         hover:bg-teal-600 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
